import { InputHTMLAttributes, useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import Button from "./Button";
import {
  brandReadOnlyClass,
  FieldLabelTone,
  fieldLabelClass,
  InputVariant,
  inputVariantClass,
} from "./fieldStyles";

export type { FieldLabelTone, InputVariant };

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "prefix"> {
  label?: string;
  value: string;
  onChange?: (value: string) => void;
  prefix?: string;
  readOnly?: boolean;
  labelTone?: FieldLabelTone;
  variant?: InputVariant;
  showPasswordToggle?: boolean;
  containerClassName?: string;
  error?: string;
}

const Input = ({
  label,
  value,
  onChange,
  prefix,
  readOnly,
  labelTone = "default",
  variant = "brand",
  showPasswordToggle,
  containerClassName = "",
  className = "",
  id,
  type = "text",
  error,
  ...rest
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputId =
    id ?? (label ? label.toLowerCase().replace(/\s+/g, "-") : undefined);
  const isPassword = type === "password";
  const resolvedType = isPassword && showPassword ? "text" : type;
  const usePasswordToggle = showPasswordToggle ?? isPassword;
  const showLockIcon = readOnly && !(isPassword && usePasswordToggle);
  const hasRightIcon = (isPassword && usePasswordToggle) || showLockIcon;

  const fieldClass =
    variant === "brand"
      ? `${readOnly ? brandReadOnlyClass : inputVariantClass.brand} ${
          prefix ? "pl-7" : ""
        } ${hasRightIcon ? "pr-10" : ""}`
      : `${inputVariantClass[variant]} ${
          readOnly ? "!bg-gray-100 !text-gray-500 !cursor-not-allowed" : ""
        } ${hasRightIcon ? "pr-10" : ""}`;

  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={inputId} className={fieldLabelClass[labelTone]}>
          {label}
        </label>
      )}
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
            {prefix}
          </span>
        )}
        <input
          id={inputId}
          type={resolvedType}
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange?.(e.target.value)}
          className={`${fieldClass} ${className}`}
          {...rest}
        />
        {isPassword && usePasswordToggle && !readOnly && (
          <Button
            variant="plain"
            size="none"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </Button>
        )}
        {showLockIcon && (
          <Lock
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
        )}
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

export default Input;
